import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LoginForm = ({ onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  const { login } = useAuth();

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch(`${API}/settings/login-branding`);
        if (response.ok) {
          const data = await response.json();
          setBranding(data);
        }
      } catch (error) {
        console.log('Using default branding');
      } finally {
        setBrandingLoaded(true);
      }
    };
    fetchBranding();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.username, formData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Generate background style
  const getBackgroundStyle = () => {
    if (!branding?.enabled) {
      return { backgroundColor: '#f0f9f6' }; // Default Baobab bg
    }

    let style = {};
    
    switch (branding.background_type) {
      case 'image':
        if (branding.background_image_url) {
          style = {
            backgroundImage: `url(${branding.background_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          };
        } else {
          style = { backgroundColor: branding.background_color || '#f0f9f6' };
        }
        break;
      case 'gradient':
        style = {
          background: `linear-gradient(${branding.gradient_direction || '135deg'}, ${branding.gradient_from || '#1db584'}, ${branding.gradient_to || '#16a375'})`
        };
        break;
      default:
        style = { backgroundColor: branding.background_color || '#f0f9f6' };
    }
    
    return style;
  };

  // Generate card style
  const getCardStyle = () => {
    if (!branding?.enabled) {
      return {};
    }

    let style = {
      backgroundColor: branding.card_background || '#ffffff'
    };

    if (branding.card_style === 'glass') {
      style = {
        backgroundColor: `${branding.card_background || '#ffffff'}cc`,
        backdropFilter: `blur(${branding.card_blur || 10}px)`,
        WebkitBackdropFilter: `blur(${branding.card_blur || 10}px)`,
        border: '1px solid rgba(255, 255, 255, 0.2)'
      };
    }

    return style;
  };

  // Generate button style
  const getButtonStyle = () => {
    if (!branding?.enabled) {
      return {};
    }
    return {
      backgroundColor: branding.primary_color || '#1db584',
      ':hover': {
        backgroundColor: branding.primary_color ? adjustColor(branding.primary_color, -20) : '#16a375'
      }
    };
  };

  // Don't render until branding is loaded (prevents flash)
  if (!brandingLoaded) {
    return (
      <div className="min-h-screen bg-baobab-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-baobab-primary"></div>
      </div>
    );
  }

  const isCustomized = branding?.enabled;
  const primaryColor = isCustomized ? branding.primary_color : '#1db584';
  const titleText = isCustomized ? branding.title_text : 'Baobab Connect';
  const subtitleText = isCustomized ? branding.subtitle_text : 'ERP / CRM Interne';
  const welcomeText = isCustomized ? branding.welcome_text : 'Connectez-vous à votre espace';
  const taglineText = isCustomized ? branding.tagline_text : "L'Afrique avance, nous connectons.";
  const showTagline = isCustomized ? branding.tagline_enabled : true;

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={getBackgroundStyle()}
    >
      {/* Overlay for image backgrounds */}
      {isCustomized && branding.background_type === 'image' && branding.overlay_enabled && (
        <div 
          className="absolute inset-0 z-0"
          style={{ 
            backgroundColor: branding.overlay_color || '#000000',
            opacity: branding.overlay_opacity || 0.3
          }}
        />
      )}

      <div 
        className="max-w-md w-full rounded-xl shadow-2xl p-8 m-4 relative z-10"
        style={getCardStyle()}
      >
        {/* Logo */}
        {isCustomized && branding.logo_url && (
          <div className={`mb-4 ${
            branding.logo_position === 'top-left' ? 'text-left' :
            branding.logo_position === 'top-right' ? 'text-right' : 'text-center'
          }`}>
            <img 
              src={branding.logo_url} 
              alt={branding.logo_alt || 'Logo'}
              style={{ maxWidth: `${branding.logo_max_width || 140}px`, display: 'inline-block' }}
            />
          </div>
        )}

        <div className="text-center mb-8">
          <h2 
            className="text-2xl font-bold mb-2"
            style={{ color: primaryColor }}
          >
            {titleText}
          </h2>
          <h3 className="text-xl font-semibold text-gray-900">{subtitleText}</h3>
          <p className="text-gray-600 mt-2">{welcomeText}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Nom d'utilisateur ou email
            </label>
            <input
              type="text"
              id="username"
              name="username"
              data-testid="login-username-input"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
              style={{ 
                '--tw-ring-color': primaryColor,
                borderColor: formData.username ? primaryColor : undefined
              }}
              onFocus={(e) => e.target.style.borderColor = primaryColor}
              onBlur={(e) => e.target.style.borderColor = formData.username ? primaryColor : '#d1d5db'}
              placeholder="Votre identifiant"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe
            </label>
            <input
              type="password"
              id="password"
              name="password"
              data-testid="login-password-input"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
              onFocus={(e) => e.target.style.borderColor = primaryColor}
              onBlur={(e) => e.target.style.borderColor = formData.password ? primaryColor : '#d1d5db'}
              placeholder="Votre mot de passe"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error-message">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full text-white py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="spinner mr-2"></div>
                Connexion...
              </div>
            ) : (
              'Se connecter'
            )}
          </button>
        </form>

        {showTagline && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 italic">
              "{taglineText}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
